import React, { ChangeEvent, useEffect, useRef, useState } from 'react'
import { UserListInterface, RoleListInterface } from '../../../modules/accounts/components/settings/SettingsModel';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { delete_user, get_all_users, update_user } from '../api/usersApi';
import { toast } from 'react-toastify';
import { KTIcon, KTSVG } from '../../../../_metronic/helpers';
import { get_active_roles } from '../../roles/api/rolesApi';
import { permissionCheck } from '../../../helper-functions/PermissionCheck';
import { validateImage } from '../../../helper-functions/ImageValidation';
import { LoadingProgressBar } from '../../projects/components/LoadingProgressBar';

// Helper function to get full profile picture URL
const getProfilePictureUrl = (pictureUrl: string | null | undefined): string | null => {
    if (!pictureUrl) return null;
    
    // If already a full URL (starts with http:// or https://), return as is
    if (pictureUrl.startsWith('http://') || pictureUrl.startsWith('https://')) {
        return pictureUrl;
    }
    
    // Otherwise, prepend API base URL
    let baseURL = process.env.REACT_APP_API_URL || '';
    if (!baseURL) return pictureUrl;
    
    // Remove /api from the end of baseURL if present
    baseURL = baseURL.replace(/\/api\/?$/, '');
    
    // Remove trailing slash from baseURL and leading slash from pictureUrl
    const cleanBaseURL = baseURL.replace(/\/$/, '');
    const cleanPictureUrl = pictureUrl.replace(/^\//, '');
    
    return `${cleanBaseURL}/${cleanPictureUrl}`;
};

var user_create, user_update, user_delete, user_view, user_activity_log
function UsersList() {
    const [usersList, setUsersList] = useState<UserListInterface[]>([]);
    const [userPicture, setUserPicture] = useState<File | null>(null);
    const [roleList, setRoleList] = useState<RoleListInterface[]>([]);
    const imageRef = useRef<HTMLInputElement>(null);
    const [selectedUser, setSelectedUser] = useState<UserListInterface | null>(null)
    const [searchInput, setSearchInput] = useState('');
    const [pageSize, setPageSize] = useState(10);
    const [sortBy, setSortBy] = useState<{ key: string, ascending: boolean } | null>(null);
    const [currentPage, setCurrentPage] = useState(1);
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const [nameValidationError, setNameValidationError] = useState('');
    const [roleValidationError, setRoleValidationError] = useState('');
    const [visibleData, setVisibleData] = useState<UserListInterface[]>([]);
    
    user_create = Boolean(permissionCheck('user-create'));
    user_update = Boolean(permissionCheck('user-update'));
    user_delete = Boolean(permissionCheck('user-delete'));
    user_view = Boolean(permissionCheck('user-view'));
    user_activity_log = Boolean(permissionCheck('user-activity-log'));
    
    const { data, isLoading, isError, refetch } = useQuery('get_all_users', get_all_users, { refetchOnWindowFocus: true });
    const { data: dataRoles } = useQuery('roles', get_active_roles, { refetchOnWindowFocus: false });
    
    useEffect(() => {
        if (dataRoles) {
            setRoleList(dataRoles.data)
        }
    }, [dataRoles]);
    
    useEffect(() => {
        if (data) {
            setUsersList(data.data);
        }
    }, [data]);
    
    const totalItems = usersList.length;
    const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
    const pageSafe = Math.min(currentPage, totalPages);
    const startIdx = (pageSafe - 1) * pageSize;
    const endIdx = startIdx + pageSize;
    
    useEffect(() => {
        if (usersList.length > 0) {
            let sortedData = [...usersList];
            if (sortBy) {
                sortedData.sort((a, b) => {
                    const key = sortBy.key;
                    const aValue = a[key as keyof UserListInterface] ?? '';
                    const bValue = b[key as keyof UserListInterface] ?? '';
                    
                    if (typeof aValue === 'number' && typeof bValue === 'number') {
                        return sortBy.ascending ? aValue - bValue : bValue - aValue;
                    } else if (key.includes('_at') && typeof aValue === 'string' && typeof bValue === 'string') {
                        return sortBy.ascending
                            ? new Date(aValue).getTime() - new Date(bValue).getTime()
                            : new Date(bValue).getTime() - new Date(aValue).getTime();
                    } else {
                        return sortBy.ascending
                            ? aValue.toString().localeCompare(bValue.toString())
                            : bValue.toString().localeCompare(aValue.toString());
                    }
                });
            }
            
            setVisibleData(
                sortedData.slice(startIdx, endIdx)
            );
        }
    }, [usersList, sortBy, currentPage, pageSize, startIdx, endIdx]);
    
    useEffect(() => {
        setCurrentPage(1);
    }, [pageSize]);
    
    const handleInputChange = (e: React.FormEvent<HTMLInputElement>) => {
        const searchTerm = e.currentTarget.value;
        setSearchInput(searchTerm);
        if (data && data.data) {
            const filteredItems = data.data.filter((user: UserListInterface) => {
                return user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                    user.role?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                    user.email?.toLowerCase().includes(searchTerm.toLowerCase())
            })
            setVisibleData(filteredItems);
            setUsersList(filteredItems);
        }
    };
    
    const handleImageChange = (
        e: React.ChangeEvent<HTMLInputElement>,
        logoType: string,
        MAX_FILE_SIZE: number,
        ALLOWED_TYPES: string[],
        MAX_DIMENSIONS: { [key: string]: { width: number; height: number } }
    ) => {
        const newFile = e.target.files?.[0];
        if (newFile) {
            validateImage(
                newFile,
                logoType,
                (validFileUrl) => {
                    if (logoType === 'picture') {
                        setUserPicture(newFile);
                    }
                },
                MAX_FILE_SIZE,
                ALLOWED_TYPES,
                MAX_DIMENSIONS
            );
        }
    };
    
    const handleDeleteUser = useMutation((id: number) => {
        return delete_user(id);
    }, {
        onSuccess: (response: any) => {
            toast.success(response?.message || 'User deleted successfully', {
                position: 'top-right',
                autoClose: 3000,
            });
            queryClient.invalidateQueries('get_all_users');
            refetch();
        },
        onError: (error: any) => {
            const errorMessage = error?.response?.data?.message || 'Failed to delete user';
            toast.error(errorMessage, {
                position: 'top-right',
                autoClose: 3000,
            });
        },
    });
    
    const handleSort = (key: string) => {
        setSortBy((prev) =>
            !prev || prev.key !== key
                ? { key, ascending: true }
                : { key, ascending: !prev.ascending }
        );
    };
    
    const handleUpdateUser = useMutation((updatedUserData: any) => {
        if (updatedUserData.name.trim() === '') {
            return Promise.reject(new Error('Name is required.'));
        } else if (updatedUserData.email.trim() === '') {
            return Promise.reject(new Error('Email is required.'));
        } else if (!updatedUserData.role || updatedUserData.role.toString().trim() === '') {
            return Promise.reject(new Error('Role is required.'));
        }
        
        const formData: any = new FormData();
        if (updatedUserData.title) {
            formData.append('title', updatedUserData.title);
        }
        formData.append('name', updatedUserData.name);
        formData.append('email', updatedUserData.email);
        formData.append('role', updatedUserData.role.toString());
        formData.append('status', updatedUserData.status || '1');
        
        if (userPicture) {
            formData.append('profile_picture', userPicture);
        }
        
        return update_user(updatedUserData.id, formData)
            .then((data) => {
                toast.success(data.message || 'User updated successfully', {
                    position: 'top-right',
                    autoClose: 3000,
                });
                queryClient.invalidateQueries('get_all_users');
                refetch();
                setUserPicture(null);
            })
            .catch((error) => {
                toast.error(error.response?.data?.message || 'Error occurred.', {
                    position: 'top-right',
                    autoClose: 3000,
                });
            });
    });
    
    const handleUserRoleChange = (e: ChangeEvent<HTMLSelectElement>) => {
        if (selectedUser) {
            const newRoleId = e.target.value;
            if (!newRoleId) {
                setRoleValidationError('Role is required.');
            } else {
                setRoleValidationError('');
            }
            // Find role name from roleList for display
            const selectedRole = roleList.find(r => r.id.toString() === newRoleId);
            setSelectedUser({
                ...selectedUser,
                role_id: parseInt(newRoleId),
                role: selectedRole?.name || '',
            });
        }
    };
    
    const resetValidationErrors = () => {
        setNameValidationError('');
        setRoleValidationError("");
        imageRef.current && (imageRef.current.value = '');
        setUserPicture(null);
    };
    

    // Handle for User Logs
    const handleUserLogs = (user: any) => {
        navigate("/users/single-user-log", { state: { id: user.id } });
    };
    
    return (
        <>
            {/* Loading Progress Bar */}
            <LoadingProgressBar isLoading={isLoading || handleDeleteUser.isLoading || handleUpdateUser.isLoading} />
            
            <div className='card card-xxl-stretch mx-4 mb-5 mb-xl-10'>
                <div className='card-header border-0 pt-5'>
                    <h3 className='card-title align-items-start flex-column'>
                        <span className='card-label fw-bold fs-3 mb-1'>List of Users</span>
                    </h3>
                    {
                        user_create && (
                            <div className='card-toolbar'>
                                <button
                                    data-toggle="tooltip" data-placement="bottom" title="Navigate to Add User"
                                    className='btn btn-sm btn-light-dark'
                                    onClick={() => navigate('/users/add-user')}
                                >
                                    <KTIcon iconName='plus' className='fs-3' />
                                    Add User
                                </button>
                            </div>
                        )
                    }
                </div>
                <div className='card-header border-0 pt-5'>
                    <h3 className='card-title align-items-start flex-column'>
                        <KTIcon iconName='magnifier' className='fs-1 position-absolute ms-6' />
                        <input
                            data-toggle="tooltip" data-placement="bottom" title="Search a User"
                            type='text'
                            data-kt-user-table-filter='search'
                            className='form-control form-control-solid w-250px ps-14'
                            placeholder='Search User'
                            value={searchInput}
                            onChange={handleInputChange}
                        />
                    </h3>
                </div>
                <div className='card-body py-3'>
                    <div className='table-responsive'>
                        <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4'>
                            <thead>
                                <tr className='fw-bold text-muted'>
                                    <th className='min-w-50px'>Picture<span onClick={() => handleSort('profile_picture')}> <KTIcon iconName='up-down' className='fs-4 cursor-pointer' /></span></th>
                                    <th className='min-w-120px'>Name<span onClick={() => handleSort('name')}> <KTIcon iconName='up-down' className='fs-4 cursor-pointer' /></span></th>
                                    <th className='min-w-120px'>Role<span onClick={() => handleSort('role')}> <KTIcon iconName='up-down' className='fs-4 cursor-pointer' /></span></th>
                                    <th className='min-w-150px'>Email<span onClick={() => handleSort('email')}> <KTIcon iconName='up-down' className='fs-4 cursor-pointer' /></span></th>
                                    <th className='min-w-140px'>Updated By<span onClick={() => handleSort('updated_by_name')}> <KTIcon iconName='up-down' className='fs-4 cursor-pointer' /></span></th>
                                    <th className='min-w-90px'>Updated At<span onClick={() => handleSort('updated_at')}> <KTIcon iconName='up-down' className='fs-4 cursor-pointer' /></span></th>
                                    <th className='min-w-100px text-center'>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {isError ? (
                                    <tr>
                                        <td colSpan={7}>Error loading data. Please try again later.</td>
                                    </tr>
                                ) : isLoading ? (
                                    // Skeleton Loading for Users Table
                                    <>
                                        {[1, 2, 3, 4, 5].map((skeletonIndex) => (
                                            <tr key={skeletonIndex}>
                                                <td>
                                                    <div className='placeholder placeholder-sm bg-light w-50px h-50px rounded-circle'></div>
                                                </td>
                                                <td>
                                                    <div className='placeholder placeholder-lg bg-light w-150px'></div>
                                                </td>
                                                <td>
                                                    <div className='placeholder placeholder-lg bg-light w-100px'></div>
                                                </td>
                                                <td>
                                                    <div className='placeholder placeholder-lg bg-light w-150px'></div>
                                                </td>
                                                <td>
                                                    <div className='placeholder placeholder-sm bg-light w-150px'></div>
                                                </td>
                                                <td>
                                                    <div className='placeholder placeholder-sm bg-light w-120px'></div>
                                                </td>
                                                <td>
                                                    <div className='d-flex justify-content-center flex-shrink-0'>
                                                        <div className='placeholder placeholder-sm bg-light w-30px h-30px rounded me-1'></div>
                                                        <div className='placeholder placeholder-sm bg-light w-30px h-30px rounded me-1'></div>
                                                        <div className='placeholder placeholder-sm bg-light w-30px h-30px rounded'></div>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </>
                                ) : visibleData.length > 0 ? (
                                    visibleData.map((user) => (
                                        <tr key={user.id}>
                                            <td>
                                                <div className='symbol'>
                                                    {!user.profile_picture ? (
                                                        <div className="symbol symbol-circle symbol-50px">
                                                            <div className="symbol-label fs-1 fw-bold text-inverse-dark bg-dark">
                                                                {user.name ? user.name[0].toUpperCase() : ''}
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        <img 
                                                            alt='User Picture' 
                                                            src={getProfilePictureUrl(user.profile_picture) || ''} 
                                                            className="symbol symbol-circle symbol-50px"
                                                            onError={(e) => {
                                                                const target = e.target as HTMLImageElement;
                                                                target.style.display = 'none';
                                                                const parent = target.parentElement;
                                                                if (parent) {
                                                                    parent.innerHTML = `
                                                                        <div class="symbol symbol-circle symbol-50px">
                                                                            <div class="symbol-label fs-1 fw-bold text-inverse-dark bg-dark">
                                                                                ${user.name ? user.name[0].toUpperCase() : ''}
                                                                            </div>
                                                                        </div>
                                                                    `;
                                                                }
                                                            }}
                                                        />
                                                    )}
                                                </div>
                                            </td>
                                            <td>
                                                <div className='d-flex align-items-center'>
                                                    <div className='d-flex justify-content-start flex-column'>
                                                        <h3 className='text-dark fw-bold fs-6'>
                                                            {user.title ? `${user.title} ` : ''}{user.name}
                                                        </h3>
                                                    </div>
                                                </div>
                                            </td>
                                            <td>
                                                <div className='d-flex align-items-center'>
                                                    <div className='d-flex justify-content-start flex-column'>
                                                        <h3 className='text-dark fw-bold fs-6'>
                                                            {user.role}
                                                        </h3>
                                                    </div>
                                                </div>
                                            </td>
                                            <td>
                                                <div className='d-flex align-items-center'>
                                                    <div className='d-flex justify-content-start flex-column'>
                                                        <h3 className='text-dark fw-bold fs-6'>
                                                            {user.email}
                                                        </h3>
                                                    </div>
                                                </div>
                                            </td>
                                            <td>
                                                <div className='d-flex flex-column w-100 me-2'>
                                                    <div className='d-flex flex-stack mb-2'>
                                                        {user.updated_by_name || 'N/A'}
                                                    </div>
                                                </div>
                                            </td>
                                            <td>
                                                <div className='d-flex flex-column w-80 me-2'>
                                                    <div className='d-flex flex-stack mb-2'>
                                                        {user.updated_at ? new Date(user.updated_at).toLocaleDateString() : 'N/A'}
                                                    </div>
                                                </div>
                                            </td>
                                            <td>
                                                <div className='d-flex justify-content-center flex-shrink-0'>
                                                    {/* {user_activity_log && (
                                                        <button
                                                            onClick={() => {
                                                                handleUserLogs(user)
                                                            }}
                                                            className='btn btn-icon btn-light-dark btn-active-color-warning btn-sm me-1'
                                                            data-bs-toggle='tooltip'
                                                            data-bs-placement='top'
                                                            data-bs-custom-class='tooltip-dark'
                                                            title='User Logs'
                                                        >
                                                            <i className='bi bi-journal-text'></i>
                                                        </button>
                                                    )} */}
                                                    {user_view && (
                                                        <button
                                                            data-toggle="tooltip" data-placement="bottom" title="View User"
                                                            className='btn btn-icon btn-light-dark btn-active-color-success btn-sm me-2'
                                                            data-bs-toggle='modal'
                                                            data-bs-target='#kt_modal_view_user'
                                                            onClick={() => setSelectedUser({ ...user })}
                                                        >
                                                            <KTIcon iconName='eye' className='fs-3' />
                                                        </button>
                                                    )}
                                                    {user_update && (
                                                        <button
                                                            data-toggle="tooltip" data-placement="bottom" title="Edit User"
                                                            onClick={() => {
                                                                setSelectedUser({ ...user });
                                                                resetValidationErrors();
                                                            }}
                                                            data-bs-toggle='modal'
                                                            data-bs-target='#kt_modal_edit_user'
                                                            className='btn btn-icon btn-light-dark btn-active-color-warning btn-sm me-1'
                                                        >
                                                            <KTIcon iconName='pencil' className='fs-3' />
                                                        </button>
                                                    )}
                                                    {user_delete && (
                                                        <button
                                                            data-toggle="tooltip" data-placement="bottom" title="Delete User"
                                                            onClick={() => setSelectedUser({ ...user })}
                                                            className='btn btn-icon btn-light-dark btn-active-color-danger btn-sm'
                                                            data-bs-toggle='modal'
                                                            data-bs-target='#kt_modal_delete_user'
                                                        >
                                                            <KTIcon iconName='trash' className='fs-3' />
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan={8}>No Users Available.</td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
                
                {/* View User Modal */}
                <div className="modal fade" tabIndex={-1} id="kt_modal_view_user">
                    <div className="modal-dialog modal-dialog-centered modal-lg">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title fs-3">View User</h5>
                                <button
                                    type="button"
                                    className="btn btn-icon btn-lg btn-active-light-primary"
                                    data-bs-dismiss="modal"
                                    aria-label="Close"
                                >
                                    <KTSVG
                                        path="/media/icons/duotune/arrows/arr061.svg"
                                        className="svg-icon svg-icon-3x"
                                    />
                                </button>
                            </div>
                            <div className="modal-body px-4">
                                {selectedUser ? (
                                    <div className="row">
                                        <div className="col-md-4 d-flex align-items-center justify-content-center mb-4 mb-md-0">
                                            {!selectedUser.profile_picture ? (
                                                <div className="symbol symbol-circle symbol-150px">
                                                    <div className="symbol-label fs-1 fw-bold text-inverse-dark bg-dark">
                                                        {selectedUser.name ? selectedUser.name[0].toUpperCase() : ''}
                                                    </div>
                                                </div>
                                            ) : (
                                                <img
                                                    src={getProfilePictureUrl(selectedUser.profile_picture) || ''}
                                                    className="img-fluid edit-organisation-img rounded"
                                                    alt="User Picture"
                                                    onError={(e) => {
                                                        const target = e.target as HTMLImageElement;
                                                        target.style.display = 'none';
                                                        const parent = target.parentElement;
                                                        if (parent) {
                                                            parent.innerHTML = `
                                                                <div class="symbol symbol-circle symbol-150px">
                                                                    <div class="symbol-label fs-1 fw-bold text-inverse-dark bg-dark">
                                                                        ${selectedUser.name ? selectedUser.name[0].toUpperCase() : ''}
                                                                    </div>
                                                                </div>
                                                            `;
                                                        }
                                                    }}
                                                />
                                            )}
                                        </div>
                                        <div className="col-md-8">
                                            <div className="row mb-3">
                                                <label className="col-5 fw-bold fs-3">Title:</label>
                                                <div className="col-7 fs-3">{selectedUser.title || 'N/A'}</div>
                                            </div>
                                            <div className="row mb-3">
                                                <label className="col-5 fw-bold fs-3">Name:</label>
                                                <div className="col-7 fs-3">{selectedUser.name || 'N/A'}</div>
                                            </div>
                                            <div className="row mb-3">
                                                <label className="col-5 fw-bold fs-3">Email:</label>
                                                <div className="col-7 fs-3">{selectedUser.email}</div>
                                            </div>
                                            <div className="row mb-3">
                                                <label className="col-5 fw-bold fs-3">Role:</label>
                                                <div className="col-7 fs-3">{selectedUser.role}</div>
                                            </div>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="fs-3 text-center">No User Data Available</div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
                
                {/* Delete User Confirmation Modal */}
                <div className='modal fade' tabIndex={-1} id='kt_modal_delete_user'>
                    <div className='modal-dialog'>
                        <div className='modal-content'>
                            <div className='modal-header'>
                                <h5 className='modal-title'>Delete User Confirmation</h5>
                                <div
                                    className='btn btn-icon btn-sm btn-active-light-primary ms-2'
                                    data-bs-dismiss='modal'
                                    aria-label='Close'
                                >
                                    <KTSVG
                                        path='/media/icons/duotune/arrows/arr061.svg'
                                        className='svg-icon svg-icon-2x'
                                    />
                                </div>
                            </div>
                            <div className='modal-body'>
                                {selectedUser ? (
                                    <>
                                        <p className='mb-2'>
                                            <span className='fw-bold fs-5'>User Name:</span> {selectedUser.name}
                                        </p>
                                    </>
                                ) : null}
                            </div>
                            <div className='modal-footer'>
                                <button type='button' className='btn btn-light' data-bs-dismiss='modal'>
                                    Close
                                </button>
                                <button
                                    type='button'
                                    className='btn btn-danger'
                                    onClick={() => {
                                        if (selectedUser) {
                                            handleDeleteUser.mutate(selectedUser.id);
                                        }
                                    }}
                                    data-bs-dismiss='modal'
                                    disabled={handleDeleteUser.isLoading}
                                >
                                    {handleDeleteUser.isLoading ? (
                                        <>
                                            Deleting...
                                            <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                                        </>
                                    ) : (
                                        'Delete User'
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
                
                {/* Edit User Modal */}
                <div className='modal fade' tabIndex={-1} id='kt_modal_edit_user'>
                    <div className='modal-dialog modal-dialog-centered modal-lg'>
                        <div className='modal-content'>
                            <div className='modal-header'>
                                <h5 className='modal-title'>Edit User</h5>
                                <div
                                    className='btn btn-icon btn-sm btn-active-light-primary ms-2'
                                    data-bs-dismiss='modal'
                                    aria-label='Close'
                                >
                                    <KTSVG
                                        path='/media/icons/duotune/arrows/arr061.svg'
                                        className='svg-icon svg-icon-2x'
                                    />
                                </div>
                            </div>
                            <div className='modal-body'>
                                {selectedUser ? (
                                    <>
                                        <div className='row mb-6'>
                                            <label className='col-lg-4 col-form-label fw-bold fs-6'>
                                                <span className='required'>Role</span>
                                            </label>
                                            <div className='col-lg-8 fv-row'>
                                                <select
                                                    className='form-select form-select-solid form-select-lg fw-bold'
                                                    value={selectedUser.role_id?.toString() || ""}
                                                    onChange={(e) => {
                                                        handleUserRoleChange(e)
                                                    }}
                                                >
                                                    <option value=''>Select a Role...</option>
                                                    {Array.isArray(roleList) && roleList.map((rolType) => (
                                                        <option key={rolType.id} value={rolType.id}>
                                                            {rolType.name}
                                                        </option>
                                                    ))}
                                                </select>
                                                {roleValidationError && (
                                                    <div className='text-danger'>{roleValidationError}</div>
                                                )}
                                            </div>
                                        </div>
                                        <div className='row mb-6'>
                                            <label className='col-lg-4 col-form-label fw-bold fs-6'>
                                                Title
                                            </label>
                                            <div className='col-lg-8 fv-row'>
                                                <select
                                                    className='form-select form-select-solid form-select-lg fw-bold'
                                                    value={selectedUser.title || ""}
                                                    onChange={(e) => {
                                                        setSelectedUser({
                                                            ...selectedUser,
                                                            title: e.target.value,
                                                        })
                                                    }}
                                                >
                                                    <option value=''>Select Title...</option>
                                                    <option value='Mr.'>Mr.</option>
                                                    <option value='Mrs.'>Mrs.</option>
                                                    <option value='Ms.'>Ms.</option>
                                                </select>
                                            </div>
                                        </div>
                                        <div className='mb-2 row mb-6'>
                                            <label className='col-lg-4 ml-4 col-form-label required fw-bold fs-6'>
                                                Name
                                            </label>
                                            <div className='col-lg-8 fv-row'>
                                                <input
                                                    value={selectedUser.name || ""}
                                                    placeholder='Name'
                                                    className='form-control form-control-lg form-control-solid'
                                                    onChange={(e) => {
                                                        if (!e.target.value) {
                                                            setNameValidationError('Name is required.')
                                                        } else {
                                                            setNameValidationError('')
                                                        }
                                                        setSelectedUser({
                                                            ...selectedUser,
                                                            name: e.target.value,
                                                        })
                                                    }}
                                                    type='text'
                                                />
                                                {nameValidationError && (
                                                    <div className='text-danger'>{nameValidationError}</div>
                                                )}
                                            </div>
                                        </div>
                                        <div className='mb-2 row mb-6'>
                                            <label className='col-lg-4 ml-4 col-form-label required fw-bold fs-6'>
                                                Email
                                            </label>
                                            <div className='col-lg-8 fv-row'>
                                                <label className='form-control form-control-lg form-control-solid user-input'>
                                                    {selectedUser.email || ""}
                                                </label>
                                            </div>
                                        </div>
                                        {/* Status Field */}
                                        <div className='row mb-6'>
                                            <label className='col-lg-4 col-form-label fw-bold fs-6'>
                                                <span className='required'>Status</span>
                                            </label>
                                            <div className='col-lg-8 fv-row'>
                                                <select
                                                    className='form-select form-select-solid form-select-lg fw-bold'
                                                    value={selectedUser.status !== undefined ? selectedUser.status.toString() : '1'}
                                                    onChange={(e) => {
                                                        setSelectedUser({
                                                            ...selectedUser,
                                                            status: parseInt(e.target.value),
                                                        })
                                                    }}
                                                >
                                                    <option value='1'>Active</option>
                                                    <option value='0'>Inactive</option>
                                                </select>
                                            </div>
                                        </div>
                                        <div className='row mb-6'>
                                            <label className='col-lg-4 col-form-label fw-bold fs-6'>Picture</label>
                                            <div className='col-lg-8 fv-row'>
                                                <input
                                                    type='file'
                                                    ref={imageRef}
                                                    className='form-control form-control-lg form-control-solid'
                                                    onChange={(e) => handleImageChange(
                                                        e,
                                                        'picture',
                                                        2,
                                                        ['image/jpeg', 'image/jpg', 'image/png'],
                                                        {
                                                            picture: { width: 200, height: 200 }
                                                        }
                                                    )}
                                                    accept="image/jpeg, image/jpg, image/png"
                                                    name='picture'
                                                />
                                                <div className='text-danger mt-2'>
                                                    Recommended size: 200 x 200 px, max file size: 2MB, and accepted file types: jpg, jpeg, or png only.
                                                </div>
                                                {selectedUser?.profile_picture ? (
                                                    <div className='mt-2'>
                                                        <img
                                                            src={getProfilePictureUrl(selectedUser?.profile_picture) || ''}
                                                            alt='User Picture'
                                                            className='symbol'
                                                            width={200}
                                                            height={200}
                                                            onError={(e) => {
                                                                const target = e.target as HTMLImageElement;
                                                                target.style.display = 'none';
                                                                const parent = target.parentElement;
                                                                if (parent) {
                                                                    parent.innerHTML = `
                                                                        <div class="symbol symbol-circle" style="width: 200px; height: 200px;">
                                                                            <div class="symbol-label fs-1 fw-bold text-inverse-dark bg-dark">
                                                                                ${selectedUser.name ? selectedUser.name[0].toUpperCase() : ''}
                                                                            </div>
                                                                        </div>
                                                                    `;
                                                                }
                                                            }}
                                                        />
                                                    </div>
                                                ) : (
                                                    <div className='mt-2 text-muted'>No existing image</div>
                                                )}
                                            </div>
                                        </div>
                                    </>
                                ) : null}
                            </div>
                            <div className='modal-footer'>
                                <button type='button' className='btn btn-light' data-bs-dismiss='modal'>
                                    Close
                                </button>
                                {selectedUser?.name === '' || selectedUser?.email === '' || selectedUser?.role === '' ? (
                                    <button type='button' className='btn btn-light-dark' disabled={true}>
                                        Update User
                                    </button>
                                ) : (
                                    <button
                                        type='button'
                                        className='btn btn-primary'
                                        onClick={() => {
                                            if (selectedUser && selectedUser.name && selectedUser.email) {
                                                handleUpdateUser.mutateAsync({
                                                    id: selectedUser.id,
                                                    title: selectedUser.title,
                                                    name: selectedUser.name,
                                                    email: selectedUser.email,
                                                    role: selectedUser.role_id?.toString() || selectedUser.role,
                                                    status: selectedUser.status !== undefined ? selectedUser.status.toString() : '1',
                                                    userPicture: userPicture
                                                })
                                            }
                                        }}
                                        data-bs-dismiss='modal'
                                        disabled={handleUpdateUser.isLoading}
                                    >
                                        {handleUpdateUser.isLoading ? (
                                            <>
                                                Updating...
                                                <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                                            </>
                                        ) : (
                                            'Update User'
                                        )}
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
                
                {/* Pagination controls */}
                <div className='d-flex align-items-center justify-content-between mt-4 py-4 px-4'>
                    <div className='d-flex align-items-center gap-3'>
                        <span className='text-muted fs-7'>
                            Showing {usersList.length === 0 ? 0 : startIdx + 1}-{Math.min(endIdx, usersList.length)} of {usersList.length}
                        </span>
                        <div className='d-flex align-items-center'>
                            <span className='me-2 text-muted fs-7'>Show</span>
                            <select
                                className='form-select form-select-sm w-auto'
                                value={pageSize}
                                onChange={(e) => setPageSize(Number(e.target.value))}
                            >
                                <option value={10}>10</option>
                                <option value={20}>20</option>
                                <option value={50}>50</option>
                            </select>
                            <span className='ms-2 text-muted fs-7'>per page</span>
                        </div>
                    </div>
                    <ul className='pagination mb-0'>
                        <li className={`page-item ${pageSafe === 1 ? 'disabled' : ''}`}>
                            <button className='page-link' onClick={() => setCurrentPage(1)}>«</button>
                        </li>
                        <li className={`page-item ${pageSafe === 1 ? 'disabled' : ''}`}>
                            <button className='page-link' onClick={() => setCurrentPage(p => Math.max(1, p - 1))}>Prev</button>
                        </li>
                        <li className='page-item active'>
                            <span className='page-link'>{pageSafe} / {totalPages}</span>
                        </li>
                        <li className={`page-item ${pageSafe === totalPages ? 'disabled' : ''}`}>
                            <button className='page-link' onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}>Next</button>
                        </li>
                        <li className={`page-item ${pageSafe === totalPages ? 'disabled' : ''}`}>
                            <button className='page-link' onClick={() => setCurrentPage(totalPages)}>»</button>
                        </li>
                    </ul>
                </div>
            </div>
        </>
    )
};
export default UsersList

